import { Alert, Button, Card, DatePicker, Form, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type CalendarDay = {
  id: string;
  session_id: string;
  calendar_date: string;
  day_kind: string;
  name?: string | null;
  session_code?: string | null;
};

const DAY_KINDS = ['working', 'holiday', 'non_instructional', 'exam_window'] as const;

function kindColor(kind: string): string | undefined {
  if (kind === 'holiday') return 'red';
  if (kind === 'non_instructional') return 'orange';
  if (kind === 'exam_window') return 'purple';
  return 'green';
}

/** School calendar days per academic session. Holiday and non-instructional days feed the attendance holiday policy. */
export default function SchoolCalendarPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = Boolean(useAuthStore((state) => state.user?.permissions?.includes('education.attendance.manage')));
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [form] = Form.useForm<{ calendar_date: dayjs.Dayjs; day_kind: string; name?: string }>();
  const fail = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const days = useQuery({
    queryKey: ['education-school-calendar', sessionId],
    enabled: Boolean(sessionId),
    queryFn: () => api.get<CalendarDay[]>('/education/attendance/calendar', { params: { session_id: sessionId } }),
  });
  const rows = Array.isArray(days.data?.data) ? days.data!.data : [];

  const save = useMutation({
    mutationFn: (values: { calendar_date: dayjs.Dayjs; day_kind: string; name?: string }) =>
      api.post('/education/attendance/calendar', {
        session_id: sessionId,
        calendar_date: values.calendar_date.format('YYYY-MM-DD'),
        day_kind: values.day_kind,
        name: values.name || undefined,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      form.resetFields(['calendar_date', 'name']);
      queryClient.invalidateQueries({ queryKey: ['education-school-calendar', sessionId] });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (dayId: string) => api.delete(`/education/attendance/calendar/${dayId}`),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-school-calendar', sessionId] });
    },
    onError: fail,
  });

  return (
    <EducationPageChrome
      title={t('schoolCalendar', { defaultValue: 'School calendar' })}
      description={t('schoolCalendarIntro', {
        defaultValue:
          'Working days, holidays, non-instructional days, and exam windows per session. Holidays and non-instructional days are blocked for attendance unless the holiday policy allows an explicit reason.',
      })}
      extra={<Button onClick={() => navigate('/education/attendance')}>{t('backToAttendance')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space wrap>
            <span>{t('session')}</span>
            <EducationSessionSelect
              value={sessionId}
              onChange={(value) => setSessionId((value as string | undefined) || undefined)}
              style={{ width: 280 }}
            />
          </Space>
        </Card>

        {!sessionId ? <Alert type="info" showIcon message={t('schoolCalendarPickSession', { defaultValue: 'Choose a session to see its calendar.' })} /> : null}

        {sessionId && canManage ? (
          <Card size="small" title={t('schoolCalendarAddDay', { defaultValue: 'Add or change a day' })}>
            <Form form={form} layout="inline" initialValues={{ day_kind: 'holiday' }} onFinish={(values) => save.mutate(values)}>
              <Form.Item name="calendar_date" rules={[{ required: true }]}>
                <DatePicker format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="day_kind" rules={[{ required: true }]}>
                <Select style={{ width: 190 }} options={DAY_KINDS.map((value) => ({ value, label: value }))} />
              </Form.Item>
              <Form.Item name="name">
                <Input maxLength={200} placeholder={t('name')} style={{ width: 220 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}

        {sessionId ? (
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              loading={days.isLoading}
              dataSource={rows}
              pagination={{ pageSize: 25 }}
              locale={{ emptyText: t('schoolCalendarEmpty', { defaultValue: 'No calendar days recorded. Every date is a normal instructional day.' }) }}
              columns={[
                { title: t('date', { defaultValue: 'Date' }), dataIndex: 'calendar_date', width: 140, render: (v: string) => String(v).slice(0, 10) },
                { title: t('type', { defaultValue: 'Type' }), dataIndex: 'day_kind', width: 180, render: (v: string) => <Tag color={kindColor(v)}>{v}</Tag> },
                { title: t('name'), dataIndex: 'name', render: (v?: string | null) => v || '—' },
                ...(canManage
                  ? [
                      {
                        title: '',
                        width: 100,
                        render: (_v: unknown, row: CalendarDay) => (
                          <Popconfirm
                            title={t('schoolCalendarRemoveConfirm', { defaultValue: 'Remove this day from the calendar?' })}
                            onConfirm={() => remove.mutate(row.id)}
                          >
                            <Button type="link" danger size="small">
                              {t('remove', { defaultValue: 'Remove' })}
                            </Button>
                          </Popconfirm>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
